<template>
  <div class="box" style="position: relative">
    <div class="level">
      <div class="level-left">
        <div class="level-item">
          <h1 class="title is-3">Current Subscription</h1>
        </div>
      </div>
      <div class="level-right">
        <div class="level-item">
          <b-button
            v-if="!isForAdmin"
            type="is-primary is-light"
            tag="router-link"
            :to="{ name: 'PlanList' }"
          >
            Change Plan
          </b-button>
        </div>
      </div>
    </div>

    <div
      v-if="hasPastDueSubscription"
      class="notification is-danger is-light has-text-centered mb-0"
      style="border-radius: 0"
    >
      <span>
        Your subscription is past due. Please
        <a href="#" @click="onManageSubscription"><strong>Update Payment Method</strong></a>
        .
      </span>
    </div>

    <hr />
    <div v-if="subscription">
      <div class="columns">
        <div class="column">
          <p class="title is-4">Plan</p>
          <div class="block">
            <strong class="has-text-info">
              {{ subscription.plan_name }}
              <span v-if="subscription.has_feature_reimbursement">(w/ Reimbursements)</span>
              <span v-if="subscription.has_blanket_vob">
                (w/ {{ subscription.blanket_vob_plan_name }})
              </span>
            </strong>
            <br />
            Billed Monthly
            <br />
            <span>
              Inquiries: {{ subscription.used_inquiries }} used of
              {{ subscription.plan_base_inquiries }}
            </span>
            <span v-if="shouldShowBlanketVobUsage">
              <br />
              <span :class="subscription.has_blanket_vob ? '' : 'has-text-grey'">
                {{ subscription.blanket_vob_plan_name }} :
                {{
                  blanketVOBUsageInfo
                    ? blanketVOBUsageInfo.used
                    : subscription.blanket_vob_used_inquiries
                }}
                used of
                {{
                  blanketVOBUsageInfo
                    ? blanketVOBUsageInfo.total
                    : subscription.blanket_vob_base_inquiries
                }}
                <span
                  v-if="blanketVOBUsageInfo && blanketVOBUsageInfo.isCancelled"
                  class="has-text-danger"
                >
                  (Cancelled)
                </span>
                <span
                  v-else-if="blanketVOBUsageInfo && !blanketVOBUsageInfo.isActive"
                  class="has-text-danger"
                >
                  (Inactive)
                </span>
                <span
                  v-else-if="
                    !subscription.has_blanket_vob &&
                    blanketVOBUsageInfo &&
                    blanketVOBUsageInfo.remaining > 0
                  "
                  class="has-text-info"
                >
                  ({{ blanketVOBUsageInfo.remaining }} remaining)
                </span>
              </span>
            </span>
          </div>
        </div>
        <div class="column">
          <p class="title is-4">Subscription</p>
          <div v-if="subscription.cancel_at_period_end">
            <strong class="has-text-danger">Your subscription will not renew.</strong>
            <br />
            Subscription ends: {{ subscription.cancel_at | formatDate }}
          </div>
          <div v-else>
            Your subscription will renew automatically.
            <br />
            Next bill date: {{ subscription.next_bill_date | formatDate }}
          </div>
        </div>
        <div class="column">
          <p class="title is-4">Payment Method</p>
          Type: {{ subscription.payment_method }}
          <br />
          <div v-if="subscription.payment_method.toLowerCase() == 'card'">
            {{ subscription.card_brand | title }} •••• {{ subscription.card_last4 }}
            <br />
            {{ subscription.card_holder }}
            <span v-if="subscription.card_holder">|</span>
            Expires: {{ subscription.card_exp_month }}/{{ subscription.card_exp_year }}
            <br />
          </div>
          <a href="#" v-if="!isForAdmin" @click="onManageSubscription">Update Payment Method</a>
          |
          <a href="#" v-if="!isForAdmin" @click="onManageSubscription">View Reciepts</a>
        </div>
      </div>
    </div>
    <div v-else>No Active Subscrition</div>
  </div>
</template>

<script>
import request from '../request';
import { mapGetters } from 'vuex';

export default {
  name: 'SubscriptionDetail',
  props: {
    isForAdmin: {
      type: Boolean,
      default: false,
    },
    subscription: {
      required: false,
      type: Object,
    },
  },
  computed: {
    ...mapGetters(['hasPastDueSubscription']),
    blanketVOBSubscriptionProduct() {
      if (!this.subscription || !this.subscription.subscription_products) {
        return null;
      }
      const activeBlanketVobProduct = this.subscription.subscription_products.find(
        (product) => product.is_active && product.plan_type === 'blanket_vob'
      );
      if (activeBlanketVobProduct) return activeBlanketVobProduct;

      const activeBlanketVobTrialProduct = this.subscription.subscription_products.find(
        (product) => product.is_active && product.plan_type === 'blanket_vob_trial'
      );
      if (activeBlanketVobTrialProduct) return activeBlanketVobTrialProduct;

      return null;
    },
    shouldShowBlanketVobUsage() {
      if (!this.subscription) return false;

      // Check if user has active blanket VOB subscription
      if (this.subscription.has_blanket_vob) {
        return true;
      }

      // Check if there's a blanket VOB subscription product with remaining quota
      const product = this.blanketVOBSubscriptionProduct;
      if (product && product.remaining_quota > 0) {
        return true;
      }

      // Fallback to legacy fields if subscription_products is not available
      if (
        this.subscription.blanket_vob_plan_id &&
        this.subscription.blanket_vob_base_inquiries -
          this.subscription.blanket_vob_used_inquiries >
          0
      ) {
        return true;
      }

      return false;
    },
    blanketVOBUsageInfo() {
      const product = this.blanketVOBSubscriptionProduct;

      if (!product) return null;

      return {
        used: product.used_quota,
        total: product.total_quota,
        remaining: product.remaining_quota,
        status: product.status,
        isActive: product.is_active,
        isCancelled: product.status === 'cancelled',
      };
    },
  },
  methods: {
    onManageSubscription() {
      this.loading = true;
      request.post('/subscriptions/portal-session').then((resp) => {
        window.location.href = resp.data.redirect_url;
      });
    },
  },
};
</script>
